/**
 * Backend messages shown on the Books pages for orders and tax (Shipping & Tax, Tax Rates, Book Orders), translated in the `serverBookOrders` namespace
 * (locales/<lng>/serverBookOrders.json). See serverMessages.js for how rules work; `t`, `label` and
 * `nested` here use this file's namespace, and `label` falls back to the shared labels in
 * `server`.
 *
 * The messages come from content-management-service on the backend's main branch. The Shipping
 * and Tax Rates pages only ever show the shared "Validation failed": their controllers answer
 * other errors as plain text, which the pages can't read as JSON, so they show their own fallback.
 * Messages that carry a third party's own words (EasyPost, a CSV download) keep those words.
 */
const rules = ({ label, nested, ANY }) => {
  // "BOOKS, TAX", the refund components the backend names in a message.
  const components = (value) =>
    value
      .split(', ')
      .map((name) => label('refundComponent', name))
      .join(', ');

  return [
    // ---- PUT admin/book-orders/{id}/status, bulk-status: BookOrderService.updateOrderStatus
    { match: /^Order not found with id: (?<id>\S+)$/, key: 'order.notFoundById' },
    { match: new RegExp(`^Invalid order status: (?<status>${ANY})$`), key: 'order.invalidStatus' },

    // ---- POST admin/book-orders/{id}/refund: BookOrderAdminController, BookOrderService, RefundPlan
    {
      match: 'No payment ID found for this order — cannot process refund',
      key: 'refund.noPaymentId',
    },
    {
      match: /^Order cannot be refunded in status: (?<status>[A-Z_]+)\. Nothing has been charged\.$/,
      key: 'refund.notCharged',
      values: ({ status }) => ({ status: label('orderStatus', status) }),
    },
    { match: 'This order has already been fully refunded', key: 'refund.alreadyFull' },
    {
      match:
        /^Already refunded on this order: (?<parts>[A-Z_, ]+)\. Pick only what is still outstanding\.$/,
      key: 'refund.alreadyRefunded',
      values: ({ parts }) => ({ parts: components(parts) }),
    },
    {
      match:
        'Refunding the books has to include the tax charged on them. Add Tax to the selection, or refund the tax on its own first.',
      key: 'refund.booksNeedTax',
    },
    {
      match: /^Nothing to refund: (?<parts>[A-Z_, ]+) came to \$0\.00 on this order\.$/,
      key: 'refund.nothingToRefund',
      values: ({ parts }) => ({ parts: components(parts) }),
    },
    {
      match:
        /^That would refund \$(?<amount>\S+) against an order of \$(?<total>\S+), of which \$(?<refunded>\S+) has already been refunded\. A refund cannot exceed what was charged\.$/,
      key: 'refund.exceedsCharged',
    },
    {
      match: new RegExp(
        `^Unknown refund component: (?<name>${ANY})\\. Expected any of BOOKS, SHIPPING, TAX\\.$`
      ),
      key: 'refund.unknownComponent',
    },
    // Square's refund errors come wrapped. The inside is translated by the shared payment rules
    // and, for "Failed to process refund: …", by the Ticket Operations rules (same Square call).
    {
      match: new RegExp(`^Refund failed: (?<detail>${ANY})$`),
      key: 'refund.failed',
      values: ({ detail }) => ({ detail: nested(detail) }),
    },

    // ---- POST admin/book-orders/{id}/purchase-label: BookOrderAdminController, EasyPostLabelService
    // (also each order's error in a bulk purchase)
    {
      match: 'EasyPost is not enabled. Set EASYPOST_ENABLED=true to use label purchasing.',
      key: 'labels.easyPostOffForPurchase',
    },
    { match: /^Order not found: (?<id>\S+)$/, key: 'order.notFound' },
    {
      match: new RegExp(`^Label already purchased for order: (?<order>${ANY})$`),
      key: 'labels.alreadyPurchased',
    },
    { match: 'No active warehouse found', key: 'labels.noActiveWarehouse' },
    // The detail is EasyPost's own message, which stays as EasyPost wrote it.
    {
      match: new RegExp(`^Failed to purchase shipping label: (?<detail>${ANY})$`),
      key: 'labels.purchaseFailed',
      values: ({ detail }) => ({ detail: nested(detail) }),
    },

    // ---- POST admin/book-orders/bulk-purchase-labels: BulkLabelPurchaseService
    { match: 'Select at least one order to buy labels for', key: 'labels.selectToBuy' },
    {
      match:
        /^Too many orders selected: (?<count>\d+)\. Buy labels (?<max>\d+) at a time so the request completes before the connection times out\.$/,
      key: 'labels.tooManyToBuy',
    },
    // haltedReason
    {
      match:
        'The EasyPost shipping wallet ran out of funds. Top it up, then buy labels for the remaining orders.',
      key: 'labels.walletEmpty',
    },

    // ---- POST admin/book-orders/bulk-print-labels: BookOrderAdminController, LabelDocumentService
    {
      match: 'EasyPost is not enabled. Set EASYPOST_ENABLED=true to use label printing.',
      key: 'labels.easyPostOffForPrint',
    },
    { match: 'Select at least one order to print labels for', key: 'labels.selectToPrint' },
    {
      match: /^Too many orders selected: (?<count>\d+)\. Print at most (?<max>\d+) labels at a time\.$/,
      key: 'labels.tooManyToPrint',
    },
    {
      match: 'None of the selected orders have a label to print. Buy labels first.',
      key: 'labels.noneToPrint',
    },
    {
      match: new RegExp(`^Could not build the label document: (?<detail>${ANY})$`),
      key: 'labels.documentFailed',
      values: ({ detail }) => ({ detail: nested(detail) }),
    },

    // ---- POST admin/zip-tax-rates/refresh, GET status (lastRun.message): ZipTaxImportService
    { match: 'Another ZIP tax import is already running', key: 'zipImport.alreadyRunning' },
    {
      match:
        'No source URL: pass sourceUrl in the request body or set zip-tax.import.source-url',
      key: 'zipImport.noSourceUrl',
    },
    // The detail is the download's or the parser's own error.
    { match: new RegExp(`^Fetch failed: (?<detail>${ANY})$`), key: 'zipImport.fetchFailed' },
    { match: 'Source returned an empty body', key: 'zipImport.emptyBody' },
    { match: new RegExp(`^Parse failed: (?<detail>${ANY})$`), key: 'zipImport.parseFailed' },
    {
      match:
        /^Sanity gate: only (?<rows>\d+) usable rows \(< min (?<min>\d+)\); existing rates left untouched\. If this is a small\/test file, lower minExpectedRows; otherwise check the column mapping\.$/,
      key: 'zipImport.sanityGate',
    },
    {
      match: /^fetched=(?<fetched>\d+) upserted=(?<upserted>\d+) skipped=(?<skipped>\d+)$/,
      key: 'zipImport.summary',
    },
  ];
};

const area = { ns: 'serverBookOrders', rules };

export default area;
